// @flow
import type { Saga, Task } from "redux-saga";
import {
  call,
  cancel,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeLatest,
} from "redux-saga/effects";
import { change, destroy, getFormValues } from "redux-form";
import queryString from "query-string";
import { makeApiCall } from "redux/middleware/api";
import type { State as RootState } from "redux/modules/reducer";
import type { EventContent } from "redux/api-types/EventContent";
import type { BusinessLocationRecord } from "redux/modules/createCabelasClass/records/BusinessLocationRecord";
import {
  getSelectedChapterLocation,
  hasChapterLocations,
  SELECT as SELECT_CHAPTER,
} from "redux/modules/instructorChapters/instructorChapters";
import {
  CHAPTER_EVENT_TYPES,
  SCHEDULE_CHAPTER_EVENTS_FORM,
  normalizeChapterEventType,
} from "redux/modules/instructorChapters/constants";
import { EVENTS_API_ENDPOINTS } from "redux/modules/createCabelasClass/constants";
import { lookupTimezone } from "shared/utilities/hereTimezone";
import { toEventTimezone } from "redux/modules/createCabelasClass/parsers/timezones";
import { LOADED, PENDING } from "shared/constants/status";
import { isContentForChapterLocation } from "./contentEligibility";
import { parseChapterEvents } from "./parseChapterEvents";
import type { State, Action } from "./createChapterEvents";
import {
  CONTENT,
  CONTENT_SUCCESS,
  CONTENT_ERROR,
  LOOKUPS,
  LOOKUPS_SUCCESS,
  LOOKUPS_ERROR,
  TIMEZONE,
  TIMEZONE_SUCCESS,
  TIMEZONE_ERROR,
  SUBMIT,
  PROGRESS,
  SUBMIT_SUCCESS,
  SUBMIT_ERROR,
  RESET_ACTIONS,
} from "./createChapterEvents";

type Context = {
  userId: string,
  location: BusinessLocationRecord,
  scheduling: State,
};
const currentContext = ({
  auth,
  instructorChapters,
  createChapterEvents,
}: RootState): ?Context => {
  const location = getSelectedChapterLocation(instructorChapters);
  return auth.userId &&
    hasChapterLocations(instructorChapters, auth.userId) &&
    location
    ? { userId: auth.userId, location, scheduling: createChapterEvents }
    : null;
};
const isCollection = (json: mixed): boolean =>
  !!json &&
  typeof json === "object" &&
  !json.error &&
  !json.errors &&
  Array.isArray(json.data) &&
  json.data.every(
    (item: { id?: string, attributes?: mixed }): boolean =>
      !!item &&
      typeof item.id === "string" &&
      !!item.id &&
      !!item.attributes &&
      typeof item.attributes === "object"
  );

export function* performChapterContent(action: Action): Saga<void> {
  if (action.type !== CONTENT) {
    return;
  }
  try {
    const { auth, instructorChapters } = yield select();
    if (!hasChapterLocations(instructorChapters, auth.userId)) {
      throw new Error("An assigned Chapter venue is required to load content.");
    }
    const contents = yield call(
      makeApiCall,
      "GET",
      `${EVENTS_API_ENDPOINTS.EVENT_CONTENT}?${queryString.stringify({
        "filter[eventType][_in]": JSON.stringify(CHAPTER_EVENT_TYPES),
      })}`,
      undefined,
      false
    );
    // Event Content currently returns a complete collection, not paginated results.
    if (!isCollection(contents)) {
      throw new Error("Unable to load Chapter event content. Try again.");
    }
    yield put({
      type: CONTENT_SUCCESS,
      payload: {
        ...action.payload,
        content: contents.data
          .filter((content: EventContent): boolean =>
            instructorChapters.locations.some(
              (location: BusinessLocationRecord): boolean =>
                isContentForChapterLocation(content, location)
            )
          )
          .map((content: EventContent): EventContent => ({
            ...content,
            attributes: {
              ...content.attributes,
              eventType: normalizeChapterEventType(
                content.attributes.eventType
              ),
            },
          })),
      },
    });
  } catch (error) {
    yield put({
      type: CONTENT_ERROR,
      payload: {
        ...action.payload,
        error: error?.message || "Unable to load Chapter event content.",
      },
    });
  }
}

export function* performChapterLookups(action: Action): Saga<void> {
  if (action.type !== LOOKUPS) {
    return;
  }
  try {
    const context = yield select(currentContext);
    if (!context || context.location.locationId !== action.payload.locationId) {
      throw new Error("Choose an assigned Chapter before scheduling events.");
    }
    const templates = yield call(
      makeApiCall,
      "GET",
      EVENTS_API_ENDPOINTS.BUSINESS_TEMPLATES(context.location.businessId),
      undefined,
      false
    );
    if (!isCollection(templates)) {
      throw new Error(
        "Unable to load pricing for this Chapter venue. Try again."
      );
    }
    yield put({
      type: LOOKUPS_SUCCESS,
      payload: { ...action.payload, templates: templates.data },
    });
  } catch (error) {
    yield put({
      type: LOOKUPS_ERROR,
      payload: {
        ...action.payload,
        error: error?.message || "Unable to load Chapter pricing.",
      },
    });
  }
}

export function* performChapterTimezone(action: Action): Saga<void> {
  if (action.type !== TIMEZONE) {
    return;
  }
  yield put(change(SCHEDULE_CHAPTER_EVENTS_FORM, "timezone", ""));
  const values = yield select(getFormValues(SCHEDULE_CHAPTER_EVENTS_FORM));
  const typedAddress = values?.useTypedAddress ? values.typedAddress : null;
  if (typedAddress) {
    yield put(change(SCHEDULE_CHAPTER_EVENTS_FORM, "typedAddress.lat", ""));
    yield put(change(SCHEDULE_CHAPTER_EVENTS_FORM, "typedAddress.lng", ""));
  }
  if (!action.payload.locationId) {
    return;
  }
  try {
    const context = yield select(currentContext);
    if (!context || context.location.locationId !== action.payload.locationId) {
      throw new Error("Choose an assigned Chapter venue.");
    }
    if (typedAddress) {
      yield delay(500);
    }
    const { resolved } = yield race({
      resolved: call(lookupTimezone, typedAddress || context.location.address),
      timeout: delay(15000),
    });
    const timezone = toEventTimezone(resolved?.name);
    if (
      !timezone ||
      (typedAddress && (resolved.lat == null || resolved.lng == null))
    ) {
      throw new Error(
        "The venue time zone could not be resolved. Choose it manually."
      );
    }
    if (typedAddress) {
      yield put(
        change(SCHEDULE_CHAPTER_EVENTS_FORM, "typedAddress.lat", resolved.lat)
      );
      yield put(
        change(SCHEDULE_CHAPTER_EVENTS_FORM, "typedAddress.lng", resolved.lng)
      );
    }
    yield put(change(SCHEDULE_CHAPTER_EVENTS_FORM, "timezone", timezone));
    yield put({ type: TIMEZONE_SUCCESS, payload: action.payload });
  } catch (error) {
    yield put({
      type: TIMEZONE_ERROR,
      payload: {
        ...action.payload,
        error: typedAddress
          ? "This address could not be found. Check it and try again."
          : "The venue time zone could not be resolved. Choose it manually.",
      },
    });
  }
}

export function* performPostChapterEvents(action: Action): Saga<void> {
  let attempted = false;
  let created = 0;
  let total = 0;
  try {
    const context = yield select(currentContext);
    if (
      !context ||
      context.scheduling.postRequestId !== action.payload.requestId ||
      context.scheduling.postStatus !== PENDING
    ) {
      return;
    }
    const { userId, location, scheduling } = context;
    if (
      scheduling.contentStatus !== LOADED ||
      scheduling.lookupStatus !== LOADED ||
      scheduling.locationId !== location.locationId
    ) {
      throw new Error("Reload the selected Chapter’s scheduling options.");
    }
    const values = action.payload.values || {};
    const events = parseChapterEvents(
      values,
      userId,
      location,
      scheduling.eventContent[values.eventContentId || ""],
      Object.values(scheduling.templates)
    );
    total = events.length;
    yield put({
      type: PROGRESS,
      payload: { ...action.payload, created, total },
    });
    for (const event of events) {
      attempted = true;
      const json = yield call(
        makeApiCall,
        "POST",
        EVENTS_API_ENDPOINTS.CREATE_EVENT,
        event,
        false
      );
      if (!json?.data?.id || json.error || json.errors) {
        throw new Error("The event was not confirmed by the server.");
      }
      created += 1;
      yield put({
        type: PROGRESS,
        payload: { ...action.payload, created, total },
      });
    }
    yield put({ type: SUBMIT_SUCCESS, payload: action.payload });
  } catch (error) {
    yield put({
      type: SUBMIT_ERROR,
      payload: {
        ...action.payload,
        needsReview: attempted,
        error: attempted
          ? `Confirmed ${created} of ${total} events. Stopped at date ${
              created + 1
            }. Check Submitted events before scheduling any remaining dates; the last request may have completed. No later dates were sent.`
          : error?.message || "Unable to prepare Chapter events.",
      },
    });
  }
}

function* clearChapterForm(): Saga<void> {
  yield put(destroy(SCHEDULE_CHAPTER_EVENTS_FORM));
}

export function* watchChapterScheduling(): Saga<void> {
  yield takeLatest([CONTENT, ...RESET_ACTIONS], performChapterContent);
  yield takeLatest(
    [LOOKUPS, SELECT_CHAPTER, ...RESET_ACTIONS],
    performChapterLookups
  );
  yield takeLatest(
    [TIMEZONE, SELECT_CHAPTER, ...RESET_ACTIONS],
    performChapterTimezone
  );
  yield takeLatest(RESET_ACTIONS, clearChapterForm);
  let postTask: ?Task;
  for (;;) {
    const action = yield take([
      SUBMIT,
      CONTENT,
      LOOKUPS,
      SELECT_CHAPTER,
      ...RESET_ACTIONS,
    ]);
    if (action.type !== SUBMIT) {
      if (postTask) {
        yield cancel(postTask);
        postTask = null;
      }
    } else if (!postTask?.isRunning()) {
      postTask = yield fork(performPostChapterEvents, action);
    }
  }
}
